<template>
  <div class="group-type-row">
    <p class="group-type-label">{{ groupData.label }} <span v-if="groupData.required">*</span> </p>
    <v-select
      :class="{
        'multiple-select': isMultiple,
        'select': !isMultiple,
        'is-invalid': isInvalid
      }"
      placeholder="Select"
      :disabled="isDisabledField"
      :multiple="isMultiple"
      :deselectFromDropdown="isMultiple"
      :closeOnSelect="!isMultiple"
      :searchable="false"
      :options="preparedOptions"
      :selectable="option => isSelectable(option)"
      label="name"
      v-model="value"
      @input="emitChanges"
    >
      <template #no-options>
        There is no options for {{ groupData.label }}
      </template>

      <template
        #option="{ id, name, price, max_capacity, member_count, is_allow_viewing_other_members, is_capacity_enforcement }"
        :class="price"
      >
        <span class="group-option__row">
          <span class="group-option__name">
            {{name}}
            <i v-if="is_capacity_enforcement && max_capacity && max_capacity <= member_count">(full)</i>
          </span>

          <span
            v-if="price !== 0 && !hidePrice && !groupData.hide_price"
            class="group-option__price"
          >
            <b v-if="price > 0">+</b><b v-if="price < 0">-</b>${{price | toFixedNumber}}
          </span>
        </span>

        <span
          v-if="is_allow_viewing_other_members"
          class="group-option__see-who"
          @click.stop="showGroupUsersList(id)"
        >
          See who is in this group
        </span>
      </template>
    </v-select>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import { mapGetters } from 'vuex'
import store from "@/store";

export default {
  components: {
    vSelect,
  },
  props: {
    groupData: {
      type: Object,
      default: () => {},
    },
    groupValue: {
      type: null,
    },
    groupOptions: {
      type: null,
      default: [],
    },
    isInvalid: {
      type: Boolean,
      default: false
    },
    isDisabledField: {
      type: Boolean,
      default: false
    },
  },
  filters: {
    toFixedNumber(value) {
      return value.toFixed(2).replace('-','')
    }
  },
  data() {
    return {
      value: null,
      options: [],
    }
  },
  watch: {
    // async chosenSessions() {
      // await this.prepareGroupOptions()
    // },
    'groupOptions': {
      handler(newVal) {
        this.options = newVal

        this.checkIsValueStillAvailable()
      },
      deep: true,
      immediate: true,
    },
    groupValue(val) {
      if (val == undefined) {
        this.value = null
      } else {
        this.value = val
      }
    },
  },
  async mounted() {
    await this.prepareGroupOptions()
    if (this.groupData.value) {
      const availableGroupIds = this.options.map(group => group.id)
      this.value = this.groupData.value.filter(session => availableGroupIds.includes(session.id))
    } else {
      this.value = null
    }
  },
  computed: {
    ...mapGetters({
      hidePrice: 'applicationRegistration/getHidePrice',
      chosenSessions: 'applicationRegistration/getChosenSessions',
      programId: 'applicationRegistration/getProgramID',
      applicationId: 'applicationRegistration/getApplicationId',
    }),
    preparedOptions() {
      return this.options.reduce((options, group) => {
        if (group.is_visible) {
          group.price = group.sessions.reduce((totalPrice, session) => {
            totalPrice += session.pivot_price
            return totalPrice
          }, 0)
          options.push(group)
          return options
        } else {
          return options
        }
      },[])
    },
    isMultiple() {
      return true
    },
  },
  methods: {
    checkIsValueStillAvailable() {
      if (!this.value) {
        return
      }

      const isStillValid = Array.isArray(this.value)
        ? this.value.every(selected =>
          this.options.some(option => option.id === selected.id)
        )
        : this.options.some(option => option.id === this.value?.id)

      if (!isStillValid) {
        this.value = null
        this.emitChanges()
      }
    },
    isCompatibleForSelect(newOption){
      if (this.value == null) {
        return true
      }

      if (this.value.length === 0) {
        return true
      }

      if (this.value[0].id == newOption.id) {
        return true
      }

      return !this.value[0].is_restrict_single_group_type && !newOption.is_restrict_single_group_type
    },
    isSelectable(option) {
      return option.is_allow_use_selection && this.isNotFullGroup(option) && this.isCompatibleForSelect(option)
    },
    isNotFullGroup(option) {
      if (!option.max_capacity) {
        return true
      }
      return !option.is_capacity_enforcement || (option.is_capacity_enforcement && option.max_capacity > option.member_count)
    },
    emitChanges() {
      this.groupData.value = this.value

      let result
      if (Array.isArray(this.groupData.value)) {
        result = this.groupData.value.map(a => a.name)
      } else {
        result = this.groupData.value?.name
      }

      this.$store.commit('applicationRegistration/UPDATE_ALL_SELECTED_GROUPS', this.groupData.value)
      this.$emit('change',  result)
    },
    async prepareGroupOptions() {
      const queryParams = {}
      queryParams.program_id = this.programId
      queryParams.session_id = this.chosenSessions.map(session => session.id)
      queryParams.group_type = this.groupData.default
      queryParams.application_id = this.applicationId

      await store.dispatch('applicationRegistration/fetchApplicationGroupOptions', queryParams)
        .then(res => {
          this.options = res
        })
    },
    showGroupUsersList(groupId) {
      store.commit('applicationRegistration/SET_SELECTED_GROUP_FOR_VIEWING_USERS', groupId)
      this.$bvModal.show('group-users-list')
    }
  }
}
</script>

<style lang="scss">
.group-type-label{
  margin-bottom: 10px;
}
.is-invalid .vs__dropdown-toggle {
  border: 1px solid red;
}

// Options can wrap onto multiple lines (long names / see-who link),
// so we can't rely on the shared fixed height/line-height used elsewhere
// for `.multiple-select .vs__dropdown-option`. Override it here, scoped
// to this component's dropdown only.
.group-type-row .vs__dropdown-menu li {
  height: auto;
  min-height: 60px;
  line-height: normal;
  white-space: normal;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  padding-top: 10px;
  padding-bottom: 10px;
  row-gap: 4px;

  // the checkbox pseudo-elements are positioned relative to the first
  // span (group-option__row); re-center them now that the row height
  // is no longer a fixed 60px.
  > span:nth-child(1) {
    width: 100%;

    &:before,
    &:after {
      top: 50%;
    }

    &:after {
      transform: translateY(-50%);
    }

    &:before {
      transform: translateY(-50%) rotate(45deg);
    }
  }
}

.group-option__row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}

.group-option__name {
  flex: 1 1 auto;
  min-width: 0;
  word-break: break-word;
  white-space: normal;

  // The global `.multiple-select .vs__dropdown-menu li span:nth-child(1)`
  // rule is a descendant selector, so it also matches this span (it's the
  // first child of `.group-option__row`) and draws a *second* checkbox.
  // Explicitly strip the checkbox pseudo-elements/padding from it.
  padding-left: 0 !important;

  &:before,
  &:after {
    content: none !important;
  }
}

.group-option__price {
  flex: 0 0 auto;
  white-space: nowrap;
}

.group-option__see-who {
  display: block;
  text-decoration: underline;
  cursor: pointer;
}

// Selected chips (the tags shown inside the closed control) only render
// the plain option name by default, so long names need proper wrapping
// and spacing instead of the shared fixed-height single-line styling.
.group-type-row {
  .vs__dropdown-toggle {
    height: auto;
    min-height: 60px;
    padding: 10px 20px;
  }

  .vs__selected-options {
    flex-wrap: wrap;
    gap: 8px;
    padding: 0;
  }

  .vs__selected {
    display: flex;
    align-items: center;
    gap: 8px;
    max-width: 100%;
    margin: 0;
    padding: 6px 10px;
    border-radius: 10px;
    line-height: 1.3;
    white-space: normal;
    word-break: break-word;
  }

  .vs__deselect {
    flex: 0 0 auto;
  }
}

@media (max-width: 576px) {
  .group-option__row {
    flex-wrap: wrap;
    row-gap: 4px;
  }
}
</style>